import { useCallback, useEffect, useMemo, useState } from "react";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Box from "@mui/material/Box";
import { alpha, useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import type { IPedidoDetalhe } from "interfaces/loja/pedido";
import type { TipoEntrega } from "interfaces/loja/pedidoOperacional";
import { keyLocalStorage } from "helpers/constants";
import { formatDate } from "helpers/formatDate";
import { formatCurrency } from "helpers/utils";
import { labelProdutoPedidoItem } from "utils/formatFotoDisplayName";
import type { User } from "models/users/type_user";
import {
  finalizarSeparacaoPedidoLoja,
  iniciarSeparacaoPedidoLoja,
} from "services/lojaPedidos";
import { messageFromApiError } from "utils/pedidoApiResponse";
import {
  labelStatusSeparacao,
  labelTipoEntrega,
  pedidoExpedicaoPath,
  pedidoRetiradaPath,
  resolvePedidoOperacional,
} from "utils/pedidoOperacional";
import { useMaterialUIController, setMessage } from "context";

type PedidoSeparacaoOperacionalProps = {
  pedidoId: string;
  detalhe: IPedidoDetalhe | null;
  onAfterAction: (patch?: Partial<IPedidoDetalhe>) => Promise<void>;
};

function readSessionUser(): User | null {
  try {
    const raw = localStorage.getItem(keyLocalStorage.USER_STORAGE);
    if (!raw) return null;
    return JSON.parse(raw) as User;
  } catch {
    return null;
  }
}

function pickPedidoFieldsFromApi(data: unknown): Partial<IPedidoDetalhe> {
  if (!data || typeof data !== "object") return {};
  const o = data as Record<string, unknown>;
  const keys = [
    "status",
    "statusSeparacao",
    "statusLogistico",
    "statusRetirada",
    "tipoEntrega",
    "codigoRastreio",
    "provedorFrete",
  ] as const;
  const out: Partial<IPedidoDetalhe> = {};
  for (const k of keys) {
    if (k in o) (out as Record<string, unknown>)[k] = o[k];
  }
  return out;
}

function fmtDt(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return formatDate(iso, "dd/MM/yyyy HH:mm:ss");
  } catch {
    return String(iso);
  }
}

export default function PedidoSeparacaoOperacional({
  pedidoId,
  detalhe,
  onAfterAction,
}: PedidoSeparacaoOperacionalProps) {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const borderSoft =
    mode === "dark" ? alpha("#c8e6c9", 0.45) : alpha("#7cb342", 0.22);
  const navigate = useNavigate();
  const [, dispatch] = useMaterialUIController();

  const o = useMemo(
    () =>
      detalhe
        ? resolvePedidoOperacional({
            status: detalhe.status,
            tipoEntrega: detalhe.tipoEntrega,
            statusFaturamento: detalhe.statusFaturamento,
            statusSeparacao: detalhe.statusSeparacao,
            statusLogistico: detalhe.statusLogistico,
            statusRetirada: detalhe.statusRetirada,
            pagamentos: detalhe.pagamentos,
            enderecoEntrega: detalhe.enderecoEntrega,
            enderecoEntregaId: detalhe.enderecoEntregaId,
          })
        : null,
    [detalhe]
  );

  const sep = detalhe?.separacaoPedido;
  const itens = useMemo(() => detalhe?.itens ?? [], [detalhe?.itens]);

  const [conferidos, setConferidos] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState<"iniciar" | "finalizar" | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const itemIdsKey = useMemo(() => itens.map((i) => i.id).join("|"), [itens]);

  useEffect(() => {
    setConferidos((prev) => {
      const next: Record<string, boolean> = {};
      for (const it of itens) {
        next[it.id] = prev[it.id] ?? false;
      }
      return next;
    });
  }, [itemIdsKey, itens]);

  const todosConferidos =
    itens.length > 0 && itens.every((it) => conferidos[it.id]);

  const user = useMemo(() => readSessionUser(), []);

  const responsavelLabel = useMemo(() => {
    const nome = sep?.separadoPor?.nome?.trim();
    if (nome) return nome;
    const uid = sep?.separadoPorUserId;
    if (uid && user?.id === uid) return user.nome || "Você";
    if (uid) return `${uid.slice(0, 8)}…`;
    return "—";
  }, [sep?.separadoPor?.nome, sep?.separadoPorUserId, user?.id, user?.nome]);

  const podeIniciar =
    o && o.statusFaturamento === "EMITIDO" && o.statusSeparacao === "PENDENTE";

  const podeFinalizar =
    o && o.statusFaturamento === "EMITIDO" && o.statusSeparacao === "EM_SEPARACAO";

  const bloqueadoFat = o && o.statusFaturamento !== "EMITIDO";

  const onIniciar = useCallback(async () => {
    setError(null);
    setSuccess(null);
    setSubmitting("iniciar");
    try {
      const { data, status } = await iniciarSeparacaoPedidoLoja(pedidoId);
      if (status === 200 && data.success && data.data) {
        const patch = pickPedidoFieldsFromApi(data.data);
        const now = new Date().toISOString();
        await onAfterAction({
          ...patch,
          separacaoPedido: {
            ...detalhe?.separacaoPedido,
            status: "EM_SEPARACAO",
            iniciadoEm: now,
            separadoPorUserId: user?.id ?? sep?.separadoPorUserId ?? null,
          },
        });
        setSuccess(data.message || "Separação iniciada.");
        setMessage(dispatch, {
          open: true,
          severity: "success",
          text: data.message || "Separação iniciada.",
        });
      } else {
        const msg = messageFromApiError(
          data,
          "Não foi possível iniciar a separação."
        );
        setError(msg);
        setMessage(dispatch, { open: true, severity: "error", text: msg });
      }
    } catch (e: unknown) {
      const text =
        e instanceof Error ? e.message : "Falha ao iniciar a separação.";
      setError(text);
      setMessage(dispatch, { open: true, severity: "error", text });
    } finally {
      setSubmitting(null);
    }
  }, [pedidoId, detalhe?.separacaoPedido, sep?.separadoPorUserId, user?.id, onAfterAction, dispatch]);

  const onFinalizar = useCallback(async () => {
    setError(null);
    setSuccess(null);
    if (!todosConferidos) {
      setError("Marque a conferência de todos os itens antes de finalizar.");
      return;
    }
    setSubmitting("finalizar");
    try {
      const { data, status } = await finalizarSeparacaoPedidoLoja(pedidoId);
      if (status === 200 && data.success && data.data) {
        const patch = pickPedidoFieldsFromApi(data.data);
        const now = new Date().toISOString();
        const tipo = (patch.tipoEntrega ??
          detalhe?.tipoEntrega) as TipoEntrega | undefined;
        await onAfterAction({
          ...patch,
          tipoEntrega: tipo ?? detalhe?.tipoEntrega,
          separacaoPedido: {
            ...detalhe?.separacaoPedido,
            status: "SEPARADO",
            finalizadoEm: now,
            separadoPorUserId: user?.id ?? sep?.separadoPorUserId ?? null,
          },
        });
        setSuccess(data.message || "Separação finalizada.");
        setMessage(dispatch, {
          open: true,
          severity: "success",
          text: data.message || "Separação finalizada.",
        });
      } else {
        const msg = messageFromApiError(
          data,
          "Não foi possível finalizar a separação."
        );
        setError(msg);
        setMessage(dispatch, { open: true, severity: "error", text: msg });
      }
    } catch (e: unknown) {
      const text =
        e instanceof Error ? e.message : "Falha ao finalizar a separação.";
      setError(text);
      setMessage(dispatch, { open: true, severity: "error", text });
    } finally {
      setSubmitting(null);
    }
  }, [
    pedidoId,
    todosConferidos,
    detalhe?.tipoEntrega,
    detalhe?.separacaoPedido,
    sep?.separadoPorUserId,
    user?.id,
    onAfterAction,
    dispatch,
  ]);

  const tipoEntregaAtual = (detalhe?.tipoEntrega ?? "ENTREGA") as TipoEntrega;
  const proximoPasso =
    o?.statusSeparacao === "SEPARADO"
      ? tipoEntregaAtual === "ENTREGA"
        ? {
            label: "Ir para expedição",
            to: pedidoExpedicaoPath(pedidoId),
          }
        : {
            label: "Ir para retirada",
            to: pedidoRetiradaPath(pedidoId),
          }
      : null;

  if (!detalhe || !o) return null;

  return (
    <Stack spacing={2}>
      {o.statusSeparacao === "DIVERGENCIA" && (
        <Alert severity="warning">
          Divergência na separação: confira os itens e o estoque antes de
          prosseguir.
        </Alert>
      )}
      {o.statusSeparacao === "FALTA_ESTOQUE" && (
        <Alert severity="error">
          Falta de estoque: ajuste o inventário ou o pedido antes de continuar.
        </Alert>
      )}

      {bloqueadoFat && (
        <Alert severity="info">
          O faturamento precisa estar emitido para operar a separação.
        </Alert>
      )}

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          border: `1px solid ${borderSoft}`,
          boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
        }}
      >
        <MDBox p={2.5}>
          <Typography variant="button" fontWeight={700} color="primary">
            Resumo da separação
          </Typography>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="caption" color="text.secondary">
                Status
              </Typography>
              <MDBox mt={0.5}>
                <Chip
                  size="small"
                  label={labelStatusSeparacao(o.statusSeparacao)}
                  color={
                    o.statusSeparacao === "SEPARADO"
                      ? "success"
                      : o.statusSeparacao === "EM_SEPARACAO"
                        ? "primary"
                        : o.statusSeparacao === "FALTA_ESTOQUE" ||
                            o.statusSeparacao === "DIVERGENCIA"
                          ? "warning"
                          : "default"
                  }
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="caption" color="text.secondary">
                Responsável
              </Typography>
              <Typography variant="body2" fontWeight={600} mt={0.5}>
                {responsavelLabel}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="caption" color="text.secondary">
                Tipo de entrega
              </Typography>
              <Typography variant="body2" mt={0.5}>
                {labelTipoEntrega(tipoEntregaAtual)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <Typography variant="caption" color="text.secondary">
                Início
              </Typography>
              <Typography variant="body2" mt={0.5}>
                {fmtDt(sep?.iniciadoEm)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <Typography variant="caption" color="text.secondary">
                Finalização
              </Typography>
              <Typography variant="body2" mt={0.5}>
                {fmtDt(sep?.finalizadoEm)}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Typography variant="caption" color="text.secondary">
            Observações da separação
          </Typography>
          <Typography variant="body2" mt={0.5} sx={{ whiteSpace: "pre-wrap" }}>
            {sep?.observacao?.trim() ? sep.observacao : "—"}
          </Typography>
        </MDBox>
      </Card>

      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          border: `1px solid ${borderSoft}`,
        }}
      >
        <MDBox p={2.5}>
          <Typography variant="button" fontWeight={700}>
            Itens — conferência visual
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            Confira quantidade e produto; marque cada linha ao conferir fisicamente.
          </Typography>

          <Stack spacing={1.5} mt={2}>
            {itens.map((it) => (
              <MDBox
                key={it.id}
                display="flex"
                gap={2}
                alignItems="flex-start"
                p={1.5}
                sx={{
                  borderRadius: 1,
                  border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                }}
              >
                <Box
                  component="img"
                  src={it.urlImagem || undefined}
                  alt=""
                  sx={{
                    width: 56,
                    height: 56,
                    objectFit: "cover",
                    borderRadius: 1,
                    bgcolor: "action.hover",
                    flexShrink: 0,
                  }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
                <MDBox flex={1} minWidth={0}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    noWrap
                    title={labelProdutoPedidoItem(it)}
                  >
                    {labelProdutoPedidoItem(it)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    SKU {it.sku} · Qtd {it.quantidade} ·{" "}
                    {formatCurrency(Number(it.precoUnitario))} un.
                  </Typography>
                </MDBox>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={!!conferidos[it.id]}
                      onChange={(_, v) =>
                        setConferidos((s) => ({ ...s, [it.id]: v }))
                      }
                      disabled={o.statusSeparacao === "SEPARADO"}
                    />
                  }
                  label="Conferido"
                />
              </MDBox>
            ))}
            {itens.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Nenhum item neste pedido.
              </Typography>
            )}
          </Stack>
        </MDBox>
      </Card>

      <MDBox display="flex" flexWrap="wrap" gap={1} alignItems="center">
        <MDButton
          variant="gradient"
          color="dark"
          disabled={!podeIniciar || submitting !== null}
          onClick={onIniciar}
        >
          {submitting === "iniciar" ? "Iniciando…" : "Iniciar separação"}
        </MDButton>
        <MDButton
          variant="outlined"
          color="dark"
          disabled={
            !podeFinalizar ||
            submitting !== null ||
            !todosConferidos ||
            itens.length === 0
          }
          onClick={onFinalizar}
        >
          {submitting === "finalizar" ? "Finalizando…" : "Finalizar separação"}
        </MDButton>
      </MDBox>

      {proximoPasso && (
        <Alert severity="info">
          <Stack spacing={1} alignItems="flex-start">
            <Typography variant="body2">
              Separação concluída. Próximo passo:{" "}
              {tipoEntregaAtual === "ENTREGA"
                ? "expedição (envio)."
                : "retirada (loja ou evento)."}
            </Typography>
            <MDButton
              size="small"
              variant="contained"
              color="dark"
              onClick={() => navigate(proximoPasso.to)}
            >
              {proximoPasso.label}
            </MDButton>
          </Stack>
        </Alert>
      )}
    </Stack>
  );
}
